import type { ComponentType } from 'react';
import { CalendarDays, ClipboardList, LogIn, QrCode, Ticket, UserRound } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { BrandExpectNotice } from './brand-expect-notice';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useElementHeight } from '../widget-hooks/useElementHeight';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { isClientPathEnabledByModules } from '../widget-lib/embed-client-module-gates';
import { isMobileLegalFooterPath } from '../widget-lib/mobile-shell-paths';
import { PATH } from '../widget-lib/path-routes';
import { useSafeAreaInsets } from '../widget-lib/safe-area-bridge';
import { BlueHeader } from '../widget-ui/blue-header';
import { EmbedSiteFooter } from '../widget-ui/embed-site-footer';

type BottomTab = {
  label: string;
  path: string;
  /** Дополнительные пути, на которых вкладка подсвечена. */
  activePaths?: string[];
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  requiresAuth?: boolean;
  badge?: number;
};

/** Запасная высота шапки до первого замера. */
const MOBILE_HEADER_FALLBACK_PX = 48;
/** Запасная высота нижней панели до первого замера (без safe-area). */
const MOBILE_BOTTOM_BAR_FALLBACK_PX = 60;

/**
 * Оболочка клиентского мобильного приложения (`wn=mobile` / `widget_channel=mobile`):
 * шапка с названием студии и нижняя панель из пяти разделов.
 * QR-пропуск показывается только при модуле QR в тарифе организации; тренеры и услуги — кнопками на «Расписании».
 */
export function MobileShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const {
    accessToken,
    widgetConfig,
    globalWidgetConfig,
    guestShowcaseOrg,
    effectiveModules,
    me,
    unpaidBookingsCount
  } = useEmbedBootstrap();
  const insets = useSafeAreaInsets();
  const header = useElementHeight<HTMLDivElement>(MOBILE_HEADER_FALLBACK_PX);
  const bottomBar = useElementHeight<HTMLElement>(MOBILE_BOTTOM_BAR_FALLBACK_PX);
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const modules = effectiveModules ?? me?.modules ?? null;

  const orgTitle =
    (widgetConfig?.name && widgetConfig.name.trim()) ||
    (guestShowcaseOrg?.name && guestShowcaseOrg.name.trim()) ||
    (globalWidgetConfig?.title && globalWidgetConfig.title.trim()) ||
    'DomGo';

  const tabs: BottomTab[] = [
    {
      label: 'Расписание',
      path: PATH.schedule,
      activePaths: ['/', '/sessions', PATH.trainers, PATH.services, PATH.book],
      icon: CalendarDays
    },
    ...(accessToken
      ? [{ label: 'Записи', path: PATH.bookings, icon: ClipboardList, requiresAuth: true, badge: unpaidBookingsCount }]
      : []),
    ...(accessToken && isClientPathEnabledByModules(PATH.myAccess, modules, false)
      ? [{ label: 'QR-пропуск', path: PATH.myAccess, icon: QrCode, requiresAuth: true }]
      : []),
    ...(accessToken && isClientPathEnabledByModules(PATH.mySubscriptions, modules, true)
      ? [
          {
            label: 'Абонементы',
            path: PATH.mySubscriptions,
            activePaths: [PATH.mySubscriptions],
            requiresAuth: true,
            icon: Ticket
          }
        ]
      : []),
    accessToken
      ? { label: 'Профиль', path: PATH.account, activePaths: [PATH.billing], icon: UserRound, requiresAuth: true }
      : { label: 'Вход', path: PATH.signIn, activePaths: [PATH.register, PATH.forgotPassword], icon: LogIn }
  ];

  function matches(path: string): boolean {
    if (path === '/') return pathname === '/';
    return pathname === path || pathname.startsWith(`${path}/`);
  }

  function isActive(tab: BottomTab): boolean {
    return [tab.path, ...(tab.activePaths ?? [])].some(matches);
  }

  function onTabPress(tab: BottomTab) {
    if (tab.requiresAuth && !accessToken) {
      navigate(`${PATH.signIn}${suffix}`);
      return;
    }
    navigate(`${tab.path}${suffix}`);
  }

  const showLegalFooter = isMobileLegalFooterPath(pathname);

  return (
    <div
      id="domcrm-embed-mobile"
      className="relative flex min-h-screen w-full max-w-full flex-col overflow-x-hidden bg-[var(--bg)] text-base leading-normal"
      style={{
        paddingTop: `${header.height}px`,
        paddingBottom: `${bottomBar.height}px`
      }}
    >
      <div
        ref={header.ref}
        className="fixed top-0 left-0 right-0 z-40 bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-[var(--shadow-card)]"
        style={{ paddingTop: `${insets.top}px` }}
      >
        <BlueHeader title={orgTitle} />
      </div>

      <BrandExpectNotice />
      <main
        className={`mx-auto flex w-full max-w-md flex-col px-4 pt-2 ${
          showLegalFooter ? 'pb-8' : 'flex-1 pb-6'
        }`}
      >
        <div className={showLegalFooter ? 'min-h-0' : 'min-h-0 flex-1'}>
          <Outlet />
        </div>
        {showLegalFooter ? <EmbedSiteFooter className="mt-4 shrink-0 pb-1" /> : null}
      </main>

      <nav
        ref={bottomBar.ref}
        className="fixed bottom-0 left-0 right-0 z-40 flex items-stretch border-t border-[var(--border)] bg-[var(--bg-elevated)]"
        style={{ paddingBottom: `${insets.bottom}px` }}
        aria-label="Разделы"
      >
        {tabs.map((tab) => {
          const active = isActive(tab);
          const Icon = tab.icon;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => onTabPress(tab)}
              aria-current={active ? 'page' : undefined}
              className={`relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 py-2 text-center transition-colors ${
                active ? 'text-[var(--accent)]' : 'text-[var(--text-muted)] active:text-[var(--text)]'
              }`}
            >
              <span className="relative">
                <Icon className="h-6 w-6" aria-hidden />
                {tab.badge && tab.badge > 0 ? (
                  <span className="absolute -right-2.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {tab.badge}
                  </span>
                ) : null}
              </span>
              <span className="max-w-full truncate px-0.5 text-[11px] font-medium leading-tight">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
